import React, { useState, useEffect, useRef } from 'react';
import { Modal, Form, Input, Select, Upload, Button, DatePicker, message, Row, Col, Alert, Typography } from 'antd';
import { UploadOutlined, FileTextOutlined, CameraOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useUploadDocumentMutation } from '../../store/api/documentsApi';
import { Application, Route, ApplicationCargo, Document } from '../../types';
import { documentTypeLabel } from '../../utils/enumUiLabels';
import { formatUploadDocumentModalTitle } from '../../utils/applicationDisplayFormat';
import {
  buildScopedUploadPayloadFields,
  type DocumentUploadScope,
} from '../../utils/documentUploadScope';
import { getUploadBusinessDocumentDefinitions } from '../../utils/businessDocumentDefinitions';
import {
  CUSTOM_DOCUMENT_SYSTEM_TYPE,
  resolveCustomDocumentCategoryFromUploadContext,
  stripFileExtension,
} from '../../utils/documentDisplayName';
import {
  DOCUMENT_UI_DATE_FORMAT,
} from '../../utils/documentUiFormat';

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

export const DOCUMENT_TYPE_ORIGINAL_TN = 'ORIGINAL_TN';

export const DOCUMENT_TYPE_OTHER = CUSTOM_DOCUMENT_SYSTEM_TYPE;

const OTHER_DOCUMENT_TYPE_OPTION = {
  value: DOCUMENT_TYPE_OTHER,
  stage: 'other' as const,
  fileType: 'file' as const,
  label: 'Другое...',
};

function isInvoiceDocType(docType: string | undefined): boolean {
  const dt = String(docType || '').trim().toUpperCase();
  return dt === 'INVOICE' || dt === 'CARRIER_INVOICE' || dt === 'LOADING_INVOICE';
}

function isUpdDocType(docType: string | undefined): boolean {
  const dt = String(docType || '').trim().toUpperCase();
  return dt === 'UPD_ACT' || dt === 'UPD' || dt === 'CLIENT_UPD';
}

const DOCUMENT_TYPE_OPTION_DEFS = [
  { value: 'LOADING_TN', stage: 'loading' as const, fileType: 'file' as const },
  { value: 'LOADING_PHOTO', stage: 'loading' as const, fileType: 'photo' as const },
  { value: 'INVOICE', stage: 'other' as const, fileType: 'file' as const },
  { value: 'CARRIER_CONTRACT_REQUEST', stage: 'other' as const, fileType: 'file' as const },
  { value: 'CARRIER_INVOICE', stage: 'other' as const, fileType: 'file' as const },
  { value: 'LOADING_INVOICE', stage: 'other' as const, fileType: 'file' as const },
  { value: 'SIGNED_TN', stage: 'other' as const, fileType: 'file' as const },
  { value: DOCUMENT_TYPE_ORIGINAL_TN, stage: 'other' as const, fileType: 'file' as const },
  { value: 'UPD_ACT', stage: 'other' as const, fileType: 'file' as const },
  { value: 'UNLOADING_DOC', stage: 'unloading' as const, fileType: 'file' as const },
  { value: 'UNLOADING_PHOTO', stage: 'unloading' as const, fileType: 'photo' as const },
  { value: 'RAIL_WAYBILL', stage: 'loading' as const, fileType: 'file' as const },
  { value: 'AWB', stage: 'loading' as const, fileType: 'file' as const },
  { value: 'primary_docs', stage: 'other' as const, fileType: 'file' as const },
  { value: 'POWER_OF_ATTORNEY', stage: 'other' as const, fileType: 'file' as const }
] as const;

const DOCUMENT_TYPE_OPTIONS = DOCUMENT_TYPE_OPTION_DEFS.map((o) => ({
  ...o,
  label: documentTypeLabel(o.value)
}));

interface UploadDocumentModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess?: (documents: Document[]) => void | boolean | Promise<void | boolean>;
  /** Override the standard upload toast; false lets the host own success feedback. */
  successMessage?: string | false;
  application?: Application;
  route?: Route;
  cargo?: ApplicationCargo;
  /** If true, mark uploaded document as primary documents for the application */
  isPrimaryDocs?: boolean;
  /** Preset values: documentType, stage, fileType. When set, disables documentType Select. */
  preset?: {
    documentType?: string;
    stage?: 'loading' | 'unloading' | 'other';
    fileType?: 'file' | 'photo';
  };
  /** Scoped task context for route uploads (MGDA). */
  uploadScope?: DocumentUploadScope;
}

const UploadDocumentModal: React.FC<UploadDocumentModalProps> = ({
  visible,
  onCancel,
  onSuccess,
  successMessage,
  application,
  route,
  cargo,
  isPrimaryDocs,
  preset,
  uploadScope,
}) => {
  const [form] = Form.useForm();
  const [uploadDocument, { isLoading }] = useUploadDocumentMutation();
  const [fileList, setFileList] = useState<any[]>([]);
  const customDocumentNameTouchedRef = useRef(false);

  useEffect(() => {
    if (!visible) {
      customDocumentNameTouchedRef.current = false;
    }
  }, [visible]);
  useEffect(() => {
    if (visible && preset) {
      const values: Record<string, string> = {};
      if (preset.documentType) values.documentType = preset.documentType;
      if (preset.stage) values.stage = preset.stage;
      if (preset.fileType) values.fileType = preset.fileType;
      if (Object.keys(values).length > 0) {
        form.setFieldsValue(values);
      }
    }
  }, [visible, preset, form]);

  const handleSubmit = async (values: any) => {
    if (fileList.length === 0) {
      message.error('Пожалуйста, выберите файл для загрузки');
      return;
    }

    try {
      const files = fileList.map((f) => f.originFileObj).filter(Boolean) as File[];
      const originalName = files.length === 1 ? files[0].name : undefined;
      const docType = preset?.documentType ?? values.documentType;
      const scopeFields = buildScopedUploadPayloadFields({
        route,
        application,
        uploadScope,
        documentType: docType,
      });
      const payload: any = {
        ...scopeFields,
        cargoId: cargo?.id,
        stage: preset?.stage ?? values.stage,
        comment: values.comment,
        fileType: preset?.fileType ?? values.fileType,
        documentType: docType,
        invoiceNumber: values.invoiceNumber,
        invoiceDate: values.invoiceDate ? dayjs(values.invoiceDate).toISOString() : undefined,
        invoiceAmount: values.invoiceAmount != null ? Number(values.invoiceAmount) : undefined,
        receivedDate: values.receivedDate ? dayjs(values.receivedDate).toISOString() : undefined,
        sentToClientAt: values.sentToClientAt ? dayjs(values.sentToClientAt).toISOString() : undefined,
        dueDate: values.dueDate ? dayjs(values.dueDate).toISOString() : undefined,
        files,
        originalName,
        isPrimaryDocs: Boolean(isPrimaryDocs) || docType === 'primary_docs',
      };
      if (docType === DOCUMENT_TYPE_ORIGINAL_TN) {
        payload.receivedMethod = values.receivedMethod;
        payload.trackingNumber = values.trackingNumber || undefined;
      }
      if (docType === DOCUMENT_TYPE_OTHER) {
        payload.customDocumentName = String(values.customDocumentName || '').trim();
        payload.customDocumentCategory = resolveCustomDocumentCategoryFromUploadContext({
          application,
          route,
          cargo,
        });
      }
      const uploadedDocuments = await uploadDocument(payload).unwrap();
      const hostSucceeded = (await onSuccess?.(uploadedDocuments)) !== false;
      if (hostSucceeded && successMessage !== false) {
        message.success(
          successMessage ??
            (files.length === 1
              ? 'Документ успешно загружен'
              : `Загружено документов: ${files.length}`)
        );
      }
      form.resetFields();
      setFileList([]);
      customDocumentNameTouchedRef.current = false;
      onCancel();
    } catch (error: any) {
      console.error('Ошибка загрузки документа:', error);
      const msg = error?.data?.error || error?.error || error?.message || 'Ошибка при загрузке документа';
      message.error(msg);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setFileList([]);
    customDocumentNameTouchedRef.current = false;
    onCancel();
  };

  const handleUploadChange = ({ fileList: nextFileList }: { fileList: any[] }) => {
    setFileList(nextFileList);
    const docType = preset?.documentType ?? form.getFieldValue('documentType');
    if (docType !== DOCUMENT_TYPE_OTHER || customDocumentNameTouchedRef.current) {
      return;
    }
    const existingName = String(form.getFieldValue('customDocumentName') || '').trim();
    if (existingName) {
      return;
    }
    const firstFile = nextFileList[0]?.originFileObj as File | undefined;
    if (!firstFile?.name) {
      return;
    }
    const suggested = stripFileExtension(firstFile.name);
    if (suggested) {
      form.setFieldsValue({ customDocumentName: suggested });
    }
  };

  const uploadProps = {
    beforeUpload: () => false,
    fileList,
    onChange: handleUploadChange,
    maxCount: 20,
    multiple: true,
  };

  const getModalTitle = () => {
    if (route) return `Загрузка документа для маршрута №${route.id}`;
    if (application) {
      return formatUploadDocumentModalTitle(application);
    }
    if (cargo) return `Загрузка документа для груза ${cargo.name}`;
    return 'Загрузка документа';
  };

  const documentTypeOptions = (() => {
    const businessDefinitions = getUploadBusinessDocumentDefinitions({ application, route });
    const base =
      businessDefinitions.length === 0
        ? DOCUMENT_TYPE_OPTIONS
        : businessDefinitions.map((definition) => ({
            value: definition.legacyDocumentType,
            stage: definition.stage,
            fileType: definition.fileType,
            label: definition.label,
            catalogCode: definition.catalogCode,
          }));
    return [...base, OTHER_DOCUMENT_TYPE_OPTION];
  })();

  return (
    <Modal
      title={getModalTitle()}
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={600}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          fileType: 'file',
          stage: 'other',
          documentType: route ? 'CARRIER_INVOICE' : 'INVOICE',
        }}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Как загрузить файл"
          description="1) Выберите тип документа. 2) Выберите файл. 3) Нажмите «Загрузить документ»."
        />

        <Form.Item
          name="documentType"
          label="Тип документа"
          rules={[{ required: true, message: 'Выберите тип документа' }]}
        >
          <Select
            disabled={Boolean(preset?.documentType)}
            placeholder="Выберите тип документа"
            onChange={(value) => {
              const selected = documentTypeOptions.find((item) => item.value === value);
              if (selected && value !== DOCUMENT_TYPE_OTHER) {
                form.setFieldsValue({
                  stage: selected.stage,
                  fileType: selected.fileType,
                });
              }
              if (value === DOCUMENT_TYPE_OTHER) {
                form.setFieldsValue({ stage: 'other', fileType: 'file' });
                if (customDocumentNameTouchedRef.current) {
                  return;
                }
                const existingName = String(form.getFieldValue('customDocumentName') || '').trim();
                if (existingName) {
                  return;
                }
                const firstFile = fileList[0]?.originFileObj as File | undefined;
                if (!firstFile?.name) {
                  return;
                }
                const suggested = stripFileExtension(firstFile.name);
                if (suggested) {
                  form.setFieldsValue({ customDocumentName: suggested });
                }
              }
            }}
          >
            {documentTypeOptions.map((option) => (
              <Option key={`${option.value}-${option.label}`} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          noStyle
          shouldUpdate={(prev, curr) =>
            prev.documentType !== curr.documentType || prev.fileType !== curr.fileType
          }
        >
          {({ getFieldValue }) => {
            const dt = getFieldValue('documentType') ?? preset?.documentType;
            if (dt === DOCUMENT_TYPE_OTHER) {
              return (
                <Form.Item
                  name="customDocumentName"
                  label="Название документа в системе"
                  rules={[
                    { required: true, message: 'Укажите название документа' },
                    { max: 255, message: 'Не более 255 символов' },
                  ]}
                >
                  <Input
                    placeholder="Например: Сертификат качества"
                    onChange={() => {
                      customDocumentNameTouchedRef.current = true;
                    }}
                  />
                </Form.Item>
              );
            }

            return (
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="fileType"
                    label="Тип файла"
                    rules={[{ required: true, message: 'Выберите тип файла' }]}
                  >
                    <Select disabled={Boolean(preset?.fileType)}>
                      <Option value="file">
                        <FileTextOutlined /> Документ
                      </Option>
                      <Option value="photo">
                        <CameraOutlined /> Фото
                      </Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="stage"
                    label="Этап"
                    rules={[{ required: true, message: 'Выберите этап' }]}
                  >
                    <Select disabled={Boolean(preset?.stage)}>
                      <Option value="loading">Погрузка</Option>
                      <Option value="unloading">Выгрузка</Option>
                      <Option value="other">Другое</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            );
          }}
        </Form.Item>

        <Form.Item noStyle shouldUpdate={(prev, curr) => prev.documentType !== curr.documentType}>
          {({ getFieldValue }) => {
            const dt = getFieldValue('documentType') ?? preset?.documentType;
            if (!dt || dt === DOCUMENT_TYPE_OTHER || isUpdDocType(dt)) {
              return null;
            }
            const isOriginalTN = dt === DOCUMENT_TYPE_ORIGINAL_TN;
            if (isOriginalTN) {
              return (
                <>
                  <Form.Item
                    name="receivedMethod"
                    label="Способ получения"
                    rules={[{ required: true, message: 'Выберите способ получения' }]}
                  >
                    <Select
                      placeholder="Выберите способ"
                      options={[
                        { value: 'COURIER', label: 'Курьер' },
                        { value: 'MAIL', label: 'Почта' },
                        { value: 'HAND', label: 'Лично' },
                      ]}
                    />
                  </Form.Item>
                  <Form.Item name="trackingNumber" label="Трек-номер">
                    <Input placeholder="Номер отслеживания (опционально)" />
                  </Form.Item>
                </>
              );
            }
            if (!isInvoiceDocType(dt)) return null;
            const isClientInvoice = String(dt).toUpperCase() === 'INVOICE';
            return (
              <>
                <Form.Item
                  name="invoiceNumber"
                  label="Номер счета"
                  rules={[{ required: true, message: 'Укажите номер счета' }]}
                >
                  <Input placeholder="Введите номер счета" />
                </Form.Item>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="invoiceDate"
                      label="Дата счета"
                      rules={[{ required: true, message: 'Укажите дату счета' }]}
                    >
                      <DatePicker
                        style={{ width: '100%' }}
                        placeholder="Выберите дату счета"
                        format={DOCUMENT_UI_DATE_FORMAT}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="invoiceAmount"
                      label="Сумма счета"
                      rules={[{ required: true, message: 'Укажите сумму счета' }]}
                    >
                      <Input type="number" min={0} placeholder="Введите сумму счета" />
                    </Form.Item>
                  </Col>
                </Row>
                {isClientInvoice && (
                  <>
                    <Form.Item
                      name="sentToClientAt"
                      label="Дата отправки клиенту"
                      rules={[{ required: true, message: 'Укажите дату отправки клиенту' }]}
                    >
                      <DatePicker
            style={{ width: '100%' }}
            placeholder="Выберите дату"
            format={DOCUMENT_UI_DATE_FORMAT}
          />
                    </Form.Item>
                    <Form.Item name="dueDate" label="Срок оплаты (опционально)">
                      <DatePicker
                        style={{ width: '100%' }}
                        placeholder="Выберите срок оплаты"
                        format={DOCUMENT_UI_DATE_FORMAT}
                      />
                    </Form.Item>
                  </>
                )}
              </>
            );
          }}
        </Form.Item>

        <Form.Item name="comment" label="Комментарий">
          <TextArea
            rows={3}
            placeholder="Введите комментарий к документу..."
            maxLength={500}
            showCount
          />
        </Form.Item>

        <Form.Item
          name="receivedDate"
          label="Дата получения документа"
          rules={[{ required: true, message: 'Укажите дату получения документа' }]}
        >
          <DatePicker
            style={{ width: '100%' }}
            placeholder="Выберите дату"
            format={DOCUMENT_UI_DATE_FORMAT}
          />
        </Form.Item>

        <Form.Item label="Файл" required>
          <Upload {...uploadProps}>
            <Button icon={<UploadOutlined />}>
              Выбрать файл
            </Button>
          </Upload>
          <Text type="secondary" style={{ display: 'block', marginTop: 6, fontSize: 12 }}>
            {fileList[0]?.name ? `Выбран: ${fileList[0].name}` : 'Файл не выбран'}
          </Text>
          <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
            Поддерживаемые форматы: JPG, PNG, PDF, DOC, DOCX, XLS, XLSX
          </div>
        </Form.Item>

        <Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Button
                type="default"
                onClick={handleCancel}
                style={{ width: '100%' }}
              >
                Отмена
              </Button>
            </Col>
            <Col span={12}>
              <Button
                type="primary"
                htmlType="submit"
                loading={isLoading}
                style={{ width: '100%' }}
              >
                Загрузить документ
              </Button>
            </Col>
          </Row>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default UploadDocumentModal; 